import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { downloadFile } from "../lib/download";
import { ArrowLeft, Download, Building2, Wallet } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
  if (n === null || n === undefined || n === "") return "";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Ledgers() {
  const nav = useNavigate();
  const [mode, setMode] = useState("account"); // 'account' | 'company'
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([api.get("/masters/account"), api.get("/masters/company")]);
      setAccounts(a.data);
      setCompanies(c.data);
    })();
  }, []);

  useEffect(() => {
    // Reset selection when mode changes
    setSelected("");
    setLedger(null);
  }, [mode]);

  const load = async (name) => {
    setSelected(name);
    setLoading(true);
    try {
      const url = mode === "account" ? `/ledger/account/${encodeURIComponent(name)}` : `/ledger/company/${encodeURIComponent(name)}`;
      const { data } = await api.get(url);
      setLedger(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!selected) return;
    try {
      const path = mode === "account"
        ? `/ledger/account/${encodeURIComponent(selected)}/export.xlsx`
        : `/ledger/company/${encodeURIComponent(selected)}/export.xlsx`;
      const filename = mode === "account" ? `account_${selected}.xlsx` : `company_${selected}.xlsx`;
      await downloadFile(path, filename);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const options = mode === "account" ? accounts : companies;

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-4" data-testid="ledgers-back">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="mb-6">
        <div className="section-label">Ledgers</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Account & Company Ledgers</h1>
        <p className="text-slate-500 mt-1 text-sm">Pick an account or a company to view its full ledger</p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex bg-slate-100 rounded-xl p-1 mb-4" role="tablist">
        <button
          onClick={() => setMode("account")}
          data-testid="ledger-mode-account"
          className={`px-4 h-10 rounded-lg text-sm font-semibold flex items-center gap-2 ${mode === "account" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
        >
          <Wallet size={16} /> Account
        </button>
        <button
          onClick={() => setMode("company")}
          data-testid="ledger-mode-company"
          className={`px-4 h-10 rounded-lg text-sm font-semibold flex items-center gap-2 ${mode === "company" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
        >
          <Building2 size={16} /> Company
        </button>
      </div>

      {/* Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => load(o.name)}
            data-testid={`ledger-select-${o.name}`}
            className={`h-10 px-4 rounded-lg text-sm font-medium border transition-colors ${
              selected === o.name ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
          >
            {o.name}{o.currency ? ` · ${o.currency}` : ""}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-400">Loading…</div>}

      {ledger && !loading && (
        <>
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <div className="section-label">
              {mode === "account" ? "Account" : "Company"}: <span className="text-slate-900 font-bold">{selected}</span>
            </div>
            <button
              onClick={download}
              data-testid="ledger-export-btn"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold transition-colors"
            >
              <Download size={16} /> Export Excel
            </button>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {ledger.totals.map((t) => (
              <div key={t.currency} className="border border-slate-200 rounded-xl bg-white p-4">
                <div className="section-label mb-2">{t.currency}</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-[11px] text-slate-500 uppercase">In</div>
                    <div className="tabular text-emerald-700 font-semibold">{fmt(t.money_in)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 uppercase">Out</div>
                    <div className="tabular text-red-600 font-semibold">{fmt(t.money_out)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-500 uppercase">{mode === "account" ? "Balance" : "Net"}</div>
                    <div className={`tabular font-semibold ${(t.balance ?? t.net) < 0 ? "text-red-600" : "text-slate-900"}`}>
                      {fmt(t.balance ?? t.net)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Transactions */}
          <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
            {ledger.transactions.length === 0 ? (
              <div className="p-6 text-sm text-slate-400 text-center">No transactions yet.</div>
            ) : (
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Party</th>
                    <th className="px-4 py-3">PI</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    {mode === "account" && <th className="px-4 py-3 text-right">Running Balance</th>}
                    <th className="px-4 py-3">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.transactions.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => nav(`/entry/${t.id}`)}
                      className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm text-slate-700 tabular">{(t.transaction_date || "").slice(0,10)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                          (t.ledger_direction || t.money_direction) === "Money In" || t.ledger_direction === "Transfer In" ? "bg-emerald-50 text-emerald-700" :
                          (t.ledger_direction || t.money_direction) === "Money Out" || t.ledger_direction === "Transfer Out" ? "bg-red-50 text-red-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>{t.entry_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{t.company || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{t.party_name || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{t.pi_number || "—"}</td>
                      <td className={`px-4 py-3 text-sm text-right tabular font-semibold ${
                        (t.ledger_amount ?? 0) < 0 ? "text-red-700" :
                        (t.ledger_amount ?? 0) > 0 ? "text-emerald-700" : "text-slate-700"
                      }`}>
                        {t.ledger_amount !== undefined
                          ? `${t.ledger_amount < 0 ? "−" : "+"}${t.ledger_currency || t.currency} ${fmt(Math.abs(t.ledger_amount))}`
                          : `${t.currency} ${fmt(t.amount)}`}
                      </td>
                      {mode === "account" && (
                        <td className="px-4 py-3 text-sm text-right tabular text-slate-800">
                          {t.ledger_currency || t.currency} {fmt(t.running_balance)}
                        </td>
                      )}
                      <td className="px-4 py-3 text-xs text-slate-500">{t.purpose || t.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
